/**
 * Formule dei carichi di lavoro: dose, mix, scostamento, ACWR.
 *
 * Unico posto in cui vivono. API e UI le chiamano e non ricalcolano.
 * I giorni arrivano come `YYYY-MM-DD` italiani: questo modulo non vede
 * `Date` né fusi. Quota, RPE e minuti sono interi; il carico per attributo
 * sta in centesimi di UA così l'audit è esatto.
 */

import { giorniTra } from './calendario.js';
import { z } from 'zod';
import { idSchema } from './contratti.js';

export interface VoceMix {
  attributoId: string;
  quota: number;
}

export type EsitoValidaMix = { ok: true } | { ok: false; errori: string[] };

export interface CaricoAttributo {
  attributoId: string;
  centesimiUa: number;
}

export type EtichettaScostamento = 'in_linea' | 'sotto' | 'sopra';

export interface RigaScostamentoUa {
  minutiEseguiti: number;
  rpeEseguito: number;
  minutiProgrammati: number;
  rpeTarget: number;
}

export type ClassificaBlocco = 'nessuno' | 'individuale' | 'sistemico';

export type GruppoConfronto = 'portieri' | 'movimento';

export type LetturaAcwr = 'sotto' | 'zona' | 'attenzione' | 'rischio';

export type EsitoAcwr =
  | { stato: 'dati_insufficienti' }
  | { stato: 'nessun_carico' }
  | { stato: 'ok'; acuto: number; cronico: number; valore: number; lettura: LetturaAcwr };

const MSG_QUOTA = 'La quota deve essere un intero tra 1 e 100.';
const MSG_RIPETUTO = "L'attributo è ripetuto nel mix.";
const MSG_SOMMA = 'Il mix deve sommare a 100.';
const MSG_VUOTO = 'Il mix è vuoto.';

function rpeValido(n: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= 10;
}

function minutiValidi(n: number): boolean {
  return Number.isInteger(n) && n >= 0;
}

function richiedeRpe(n: number): void {
  if (!rpeValido(n)) throw new RangeError('RPE deve essere un intero tra 1 e 10.');
}

function richiedeMinuti(n: number): void {
  if (!minutiValidi(n)) {
    throw new RangeError('I minuti devono essere un intero maggiore o uguale a 0.');
  }
}

export function validaMix(mix: readonly VoceMix[]): EsitoValidaMix {
  if (mix.length === 0) return { ok: false, errori: [MSG_VUOTO] };

  const errori: string[] = [];
  if (mix.some((v) => !Number.isInteger(v.quota) || v.quota < 1 || v.quota > 100)) {
    errori.push(MSG_QUOTA);
  }
  const ids = mix.map((v) => v.attributoId);
  if (new Set(ids).size !== ids.length) errori.push(MSG_RIPETUTO);
  if (mix.reduce((s, v) => s + v.quota, 0) !== 100) errori.push(MSG_SOMMA);

  return errori.length === 0 ? { ok: true } : { ok: false, errori };
}

/**
 * `n` quote intere a somma 100, uguali a meno di 1: il resto va alle prime.
 * È il mix di un blocco nato da un test, che carica i suoi attributi in parti
 * uguali (tre attributi: 34, 33, 33).
 */
export function quoteUguali(n: number): number[] {
  if (!Number.isInteger(n) || n < 1 || n > 100) {
    throw new RangeError('Le quote uguali vanno da 1 a 100 attributi.');
  }
  const base = Math.floor(100 / n);
  const resto = 100 - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < resto ? 1 : 0));
}

/** RPE × minuti, in UA. */
export function dose({ rpe, minuti }: { rpe: number; minuti: number }): number {
  richiedeRpe(rpe);
  richiedeMinuti(minuti);
  return rpe * minuti;
}

/** quota × dose, in centesimi di UA. */
export function carichiAttributo(mix: readonly VoceMix[], valoreDose: number): CaricoAttributo[] {
  if (!Number.isInteger(valoreDose) || valoreDose < 0) {
    throw new RangeError('La dose deve essere un intero maggiore o uguale a 0.');
  }
  return mix.map((v) => ({ attributoId: v.attributoId, centesimiUa: v.quota * valoreDose }));
}

/** Somma dei centesimi === dose × 100. */
export function audit(carichi: readonly CaricoAttributo[], valoreDose: number): boolean {
  return carichi.reduce((s, c) => s + c.centesimiUa, 0) === valoreDose * 100;
}

/** Dose eseguita − dose target, in UA intere. */
export function scostamentoUa(r: RigaScostamentoUa): number {
  return (
    dose({ rpe: r.rpeEseguito, minuti: r.minutiEseguiti }) -
    dose({ rpe: r.rpeTarget, minuti: r.minutiProgrammati })
  );
}

export function etichettaScostamento(valore: number): EtichettaScostamento {
  if (valore === 0) return 'in_linea';
  return valore < 0 ? 'sotto' : 'sopra';
}

/** True se minuti o RPE differiscono dallo snapshot. */
export function motivoVisibile(r: RigaScostamentoUa): boolean {
  richiedeRpe(r.rpeEseguito);
  richiedeRpe(r.rpeTarget);
  richiedeMinuti(r.minutiEseguiti);
  richiedeMinuti(r.minutiProgrammati);
  return r.minutiEseguiti !== r.minutiProgrammati || r.rpeEseguito !== r.rpeTarget;
}

/**
 * Somma dose eseguite − somma dose target. Null se non ci sono righe.
 */
export function deltaSeduta(righe: readonly RigaScostamentoUa[]): number | null {
  if (righe.length === 0) return null;
  return righe.reduce((s, r) => s + scostamentoUa(r), 0);
}

/** True se le 3 sedute più recenti sono sotto target e cadono in [a−13, a]. */
export function serieSottoTarget({
  sedute,
  a,
}: {
  sedute: readonly { giorno: string; delta: number }[];
  a: string;
}): boolean {
  if (sedute.length < 3) return false;
  const recenti = [...sedute].sort((x, y) => y.giorno.localeCompare(x.giorno)).slice(0, 3);
  return recenti.every((s) => s.delta < 0 && nelFinestra14(s.giorno, a));
}

function nelFinestra14(giorno: string, a: string): boolean {
  const n = giorniTra(giorno, a);
  return n >= 1 && n <= 14;
}

export function classificaBlocco(righe: readonly { scostamentoUa: number }[]): ClassificaBlocco {
  const n = righe.length;
  const k = righe.filter((r) => r.scostamentoUa > 0).length;
  if (k === 0) return 'nessuno';
  if (k >= 3 && 3 * k >= 2 * n) return 'sistemico';
  return 'individuale';
}

export function acwr({
  dosiGiornaliere,
  giorniDiStoria,
}: {
  dosiGiornaliere: readonly number[];
  giorniDiStoria: number;
}): EsitoAcwr {
  if (dosiGiornaliere.length !== 28) {
    throw new RangeError('Servono esattamente 28 dosi giornaliere.');
  }
  for (const d of dosiGiornaliere) {
    if (!Number.isInteger(d) || d < 0) {
      throw new RangeError('La dose giornaliera deve essere un intero maggiore o uguale a 0.');
    }
  }
  if (giorniDiStoria < 28) return { stato: 'dati_insufficienti' };
  const s28 = dosiGiornaliere.reduce((s, d) => s + d, 0);
  if (s28 === 0) return { stato: 'nessun_carico' };
  const s7 = dosiGiornaliere.slice(21).reduce((s, d) => s + d, 0);
  return {
    stato: 'ok',
    acuto: s7 / 7,
    cronico: s28 / 28,
    valore: (4 * s7) / s28,
    lettura: letturaAcwr(s7, s28),
  };
}

function letturaAcwr(s7: number, s28: number): LetturaAcwr {
  const num = 40 * s7;
  if (num < 8 * s28) return 'sotto';
  if (num <= 13 * s28) return 'zona';
  if (num <= 15 * s28) return 'attenzione';
  return 'rischio';
}

export function gruppoConfronto(ruolo: string): GruppoConfronto {
  return ruolo === 'portiere' ? 'portieri' : 'movimento';
}

export const FINESTRE_CARICO = ['settimana', 'mese', 'stagione'] as const;
export type FinestraCarico = (typeof FINESTRE_CARICO)[number];

export const ETICHETTA_ACWR: Record<LetturaAcwr | 'dati_insufficienti' | 'nessun_carico', string> =
  {
    sotto: 'Sotto',
    zona: 'Zona',
    attenzione: 'Attenzione',
    rischio: 'Rischio',
    dati_insufficienti: 'Dati insufficienti',
    nessun_carico: 'Nessun carico',
  };

export const filtriCarichiSchema = z
  .object({
    da: z.string().date('Data non valida.').optional(),
    a: z.string().date('Data non valida.').optional(),
    finestra: z.enum(FINESTRE_CARICO, { message: 'Finestra non valida.' }).optional(),
  })
  .refine((f) => Boolean(f.da) === Boolean(f.a), {
    message: 'Indica da e a insieme.',
  })
  .refine((f) => !f.da || !f.a || f.da <= f.a, {
    message: "La fine deve seguire l'inizio.",
  });

export type FiltriCarichi = z.output<typeof filtriCarichiSchema>;

const rpeCarico = z
  .number({ invalid_type_error: 'RPE deve essere un intero tra 1 e 10.' })
  .int()
  .min(1, 'RPE deve essere un intero tra 1 e 10.')
  .max(10, 'RPE deve essere un intero tra 1 e 10.');

export const caricoPartitaSchema = z.object({
  schedaId: idSchema,
  sostituisci: z.boolean().optional(),
  log: z.array(
    z.object({
      giocatoreId: idSchema,
      rpeEseguito: rpeCarico,
      minutiEseguiti: z
        .number({ invalid_type_error: 'I minuti devono essere un intero maggiore o uguale a 0.' })
        .int()
        .min(0, 'I minuti devono essere un intero maggiore o uguale a 0.')
        .optional(),
    }),
  ),
});

export type DatiCaricoPartita = z.infer<typeof caricoPartitaSchema>;
